import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef, useState } from 'react'
import { fontSans, hexToRgba } from './theme'

const capabilities = [
  {
    id: 'ingestion',
    number: '01',
    title: 'Agentic Ingestion',
    description:
      'Modular connectors scan your sources and stream schemas, samples and statistics into the platform. PostgreSQL, MySQL, MSSQL and CSV at runtime — Snowflake and Redshift code-ready — with a connector architecture built for expansion.',
    color: '#4A90E2',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <ellipse cx="14" cy="6" rx="9" ry="3.5" />
        <path d="M5 6v7c0 1.93 4 3.5 9 3.5s9-1.57 9-3.5V6" />
        <path d="M5 13v7c0 1.93 4 3.5 9 3.5s9-1.57 9-3.5v-7" />
      </svg>
    ),
  },
  {
    id: 'catalog',
    number: '02',
    title: 'A Living Catalog',
    description:
      'Every scan lands in a centralized, workspace-isolated catalog: table schemas, sample data, and automated profiles — row counts, column types, nullability. Semantic search puts the right table one query away.',
    color: '#9D4EDD',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="4" y="4" width="8" height="8" rx="2" />
        <rect x="16" y="16" width="8" height="8" rx="2" />
        <path d="M12 8h4a4 4 0 0 1 4 4v4" />
        <path d="M18 14l2 2-2 2" />
      </svg>
    ),
  },
  {
    id: 'governance',
    number: '03',
    title: 'Governance Built In',
    description:
      'Pattern- and name-based PII detection runs on every scan. Masking is policy-driven — hash, redact or partially mask — applied before anyone sees sample data, with audit events for every access.',
    color: '#2DD4BF',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 3 5 6.5v6c0 5.5 3.8 10.3 9 12.5 5.2-2.2 9-7 9-12.5v-6L14 3Z" />
        <path d="M10 14h8M10 17.5h5" />
      </svg>
    ),
  },
  {
    id: 'trust',
    number: '04',
    title: 'Quality, Lineage & Durable Runs',
    description:
      'Define not-null, unique, regex and range checks against cataloged data. Trace how tables and columns flow through pipelines. Runs are crash-safe: locks, stale detection and startup recovery keep every execution honest.',
    color: '#F5B850',
    icon: (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 4a10 10 0 1 0 10 10" />
        <path d="M14 4v6l5-3" />
        <path d="M20 8l4 0 0 4" />
      </svg>
    ),
  },
]

const connectors = [
  'PostgreSQL',
  'MySQL',
  'MSSQL',
  'CSV',
  'Snowflake',
  'Redshift',
]

function CapabilityCard({
  cap,
  delay,
}: {
  cap: (typeof capabilities)[number]
  delay: number
}) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  const [hovered, setHovered] = useState(false)

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '2.25rem',
        borderRadius: '20px',
        background: hovered
          ? hexToRgba(cap.color, 0.07)
          : 'rgba(255,255,255,0.025)',
        border: `1px solid ${hovered ? hexToRgba(cap.color, 0.28) : 'rgba(255,255,255,0.06)'}`,
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered ? `0 20px 60px ${hexToRgba(cap.color, 0.1)}` : 'none',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      {/* Number watermark */}
      <div
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1.5rem',
          fontFamily: fontSans,
          fontSize: '3.5rem',
          fontWeight: 800,
          color: hexToRgba(cap.color, 0.07),
          lineHeight: 1,
          userSelect: 'none',
          transition: 'color 0.3s ease',
          ...(hovered ? { color: hexToRgba(cap.color, 0.12) } : {}),
        }}
      >
        {cap.number}
      </div>

      <div
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '14px',
          background: hexToRgba(cap.color, 0.1),
          border: `1px solid ${hexToRgba(cap.color, 0.3)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.5rem',
          color: cap.color,
          transition: 'all 0.3s ease',
          ...(hovered
            ? {
                background: hexToRgba(cap.color, 0.16),
                border: `1px solid ${hexToRgba(cap.color, 0.55)}`,
                boxShadow: `0 0 20px ${hexToRgba(cap.color, 0.2)}`,
              }
            : {}),
        }}
      >
        {cap.icon}
      </div>

      <h3
        style={{
          fontFamily: fontSans,
          fontSize: '1.2rem',
          fontWeight: 700,
          color: '#ffffff',
          marginBottom: '0.75rem',
          letterSpacing: '-0.02em',
        }}
      >
        {cap.title}
      </h3>
      <p
        style={{
          fontFamily: fontSans,
          fontSize: '0.9rem',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: 1.7,
          margin: 0,
        }}
      >
        {cap.description}
      </p>

      {/* Bottom accent */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${cap.color}, transparent)`,
          opacity: hovered ? 0.6 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />
    </motion.div>
  )
}

export function Capabilities() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section
      id="platform"
      style={{
        padding: '7rem 2rem',
        background: '#0A0F1F',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(74, 144, 226, 0.3), transparent)',
        }}
      />

      {/* Background glow */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          height: '400px',
          background:
            'radial-gradient(ellipse, rgba(74,144,226,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span
              style={{
                fontFamily: fontSans,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#4A90E2',
                display: 'block',
                marginBottom: '1rem',
              }}
            >
              The Core Product
            </span>
            <h2
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                marginBottom: '1.25rem',
              }}
            >
              Your Command Center for{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Autonomous Data.
              </span>
            </h2>
            <p
              style={{
                fontFamily: fontSans,
                fontSize: '1.05rem',
                color: 'rgba(255,255,255,0.5)',
                maxWidth: '580px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              The SynKrasis platform is where every scan, profile, policy and
              pipeline lives — the governance kernel the rest of the family is
              built on.
            </p>
          </motion.div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
          }}
        >
          {capabilities.map((cap, i) => (
            <CapabilityCard key={cap.id} cap={cap} delay={i * 0.1} />
          ))}
        </div>

        {/* Connector strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          style={{
            marginTop: '3rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: fontSans,
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.35)',
              marginRight: '0.25rem',
            }}
          >
            Connectors
          </span>
          {connectors.map((c) => (
            <span
              key={c}
              style={{
                fontFamily: fontSans,
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.65)',
                padding: '6px 14px',
                borderRadius: '100px',
                background: 'rgba(74, 144, 226, 0.08)',
                border: '1px solid rgba(74, 144, 226, 0.2)',
              }}
            >
              {c}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
